'use client'

import { CalendarDaysIcon, LockClosedIcon } from '@heroicons/react/20/solid'
import { useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import { addDays, format, isValid } from 'date-fns'
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Variants,
} from 'framer-motion'
import { Form, getIn, useField, useFormikContext } from 'formik'
import Link from 'next/link'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { StartTfaChallenge, VerifyTfaChallenge } from '@/lib/apis/tfa'
import useApiClient from '@/lib/apis/clients/useApiClient'
import type { PatientProfile, ProfileStatus } from '@/lib/apis/types'
import { PricingValue } from '@/lib/cms/pricing'
import { WebConfig } from '@/lib/config'
import type {
  Question,
  Questionnaire,
  QuestionnairePage,
} from '@/lib/questionnaires/Questionnaire'
import { CarerForMap, GENDERS } from '@/lib/types'
import { formatDate, parseDate } from '@/lib/utils'
import DatePickerCalendar, {
  type DatePickerCalendarValue,
} from '@/shared/components/DatePickerCalendar'
import Spinner from '@/shared/components/Spinner'
import {
  softEditorialEase,
  softEditorialEaseInOut,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'
import { AU_STATES } from '@/shared/forms/components/PersonalInformationFormFields'
import { extractFieldIds } from '@/shared/containers/QuestionnairePageContainer'
import { useTfaStore } from '@/features/tfa/store'
import QuizAddressAutocomplete, {
  type QuizAddressPrediction,
} from '@/features/quiz/inputs/QuizAddressAutocomplete'
import {
  QuizReviewSection,
  QuizReviewSections,
} from '@/shared/components/quiz/QuizReviewSection'
import { UpdateUserProfile } from '@/lib/apis/profile'
import type { PatientProfileForm } from '@/lib/apis/validators'

type ProfileLikeForm = {
  mobile: string
  firstName: string
  lastName: string
  email: string
  gender: string
  dob: string
  formattedAddress: string
  suburb: string
  state: string
  postcode: string
  [key: string]: unknown
}

type Props<T extends ProfileLikeForm> = {
  profileStatus: ProfileStatus | null
  initialProfile: PatientProfile | null
  questionnaire: Questionnaire
  certificateType: 'SICK' | 'CARER'
  multipleDays: boolean
  authenticated: boolean
}

type MobileVerificationStatus = {
  address: string
  verified: boolean
}

const STEP_COPY = [
  {
    eyebrow: 'Certificate',
    title: "Let's set up your certificate.",
    accent: 'orange' as const,
  },
  {
    eyebrow: 'Certificate',
    title: 'A quick safety check.',
    accent: 'orange' as const,
  },
  {
    eyebrow: 'Quick checks',
    title: 'A few quick checks.',
    accent: 'green' as const,
  },
] as const

const SERIOUS_SYMPTOMS = [
  'Chest pain, palpitations or shortness of breath',
  'Weakness or a change in sensation anywhere in your body',
  'Blurred vision or slurred speech',
  'A headache with drowsiness, or one that feels different from your usual migraine or tension headache',
  'Neck stiffness',
  "A rash that doesn't fade when you press on it",
  "Vomiting that won't settle, or you can't keep fluids down",
  'A fever above 39°C with shaking or chills',
  'Thoughts of self-harm or suicide',
]

const CHILD_SERIOUS_SYMPTOMS = [
  'Signs of dehydration, including fewer wet nappies, a dry mouth or unusual drowsiness',
  'A rash with drowsiness or neck stiffness',
  'Being unable to feed or keep fluids down',
]

const SYMPTOM_LABELS: Record<string, string> = {
  COMMON_COLD: 'Common cold, flu or COVID',
  PERIOD_PAIN: 'Period pain',
  MILD_LOWER_BACK_PAIN: 'Mild lower back pain from muscle strain',
  MILD_HEADACHE: 'Mild headache or migraine, same as before',
  MINOR_INJURY: 'Minor injury',
  SORE_THROAT: 'Sore throat, but tolerating food and drink',
  DIARRHOEA: 'Diarrhoea, without blood or mucus',
  TOOTHACHE: 'Toothache, awaiting dentist review',
  EXHAUSTING: 'Exhaustion or stress',
  OTHERS: 'Other mild symptoms',
}

const RADIO_LABELS: Record<string, string> = {
  Study: 'School or university',
  FamilyMember: 'Family member',
  NotApplicable: 'Not applicable',
  ...SYMPTOM_LABELS,
}

const PROFILE_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'mobile',
  'formattedAddress',
  'suburb',
  'state',
  'postcode',
  'dob',
  'gender',
  'concessionNumber',
  'entitlementNumber',
] as const

const stepMotion: Variants = {
  initial: (direction: number) => ({
    opacity: 0,
    y: direction >= 0 ? 16 : -10,
  }),
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: softMotionDuration.panel,
      ease: softEditorialEase,
    },
  },
  exit: (direction: number) => ({
    opacity: 0,
    y: direction >= 0 ? -8 : 10,
    transition: {
      duration: softMotionDuration.page,
      ease: softEditorialEaseInOut,
    },
  }),
}

const questionListMotion: Variants = {
  initial: {},
  animate: {
    transition: {
      delayChildren: 0.08,
      staggerChildren: 0.075,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.035,
      staggerDirection: -1,
    },
  },
}

const questionItemMotion: Variants = {
  initial: { opacity: 0, y: 12 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: softMotionDuration.panel,
      ease: softEditorialEase,
    },
  },
  exit: {
    opacity: 0,
    y: -6,
    transition: {
      duration: 0.34,
      ease: softEditorialEaseInOut,
    },
  },
}

const footerActionsMotion: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: softMotionDuration.handoff,
      ease: softEditorialEase,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: softMotionDuration.panel,
      ease: softEditorialEaseInOut,
    },
  },
}

export default function MedicalCertificateQuestionnaireContainer<
  T extends ProfileLikeForm,
>({
  profileStatus,
  initialProfile,
  questionnaire,
  certificateType,
  multipleDays,
  authenticated,
}: Props<T>) {
  const [[page, direction], setPage] = useState([0, 0])
  const [isAdvancing, setIsAdvancing] = useState(false)
  const advancingRef = useRef(false)
  const { values, isSubmitting, setFieldTouched, setFieldValue, validateForm } =
    useFormikContext<T>()
  const prefersReducedMotion = useReducedMotion()
  const [mobileVerificationStatus, setMobileVerificationStatus] =
    useState<MobileVerificationStatus>({
      address: initialProfile?.mobile ?? '',
      verified: profileStatus?.mobileVerified ?? false,
    })

  const startTfaVerification = useTfaStore(
    (state) => state.startTfaVerification,
  )
  const api = useApiClient()
  const startTfaChallengeMutation = useMutation(StartTfaChallenge.mutation(api))
  const verifyTfaChallengeMutation = useMutation(
    VerifyTfaChallenge.mutation(api),
  )
  const updateProfileMutation = useMutation(UpdateUserProfile.mutation(api))

  async function saveAuthenticatedProfile(mobileVerificationToken?: string) {
    if (!authenticated) return
    const profile = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: initialProfile?.email ?? values.email,
      mobile: values.mobile,
      gender: values.gender,
      dob: values.dob,
      formattedAddress: values.formattedAddress,
      suburb: values.suburb,
      state: values.state,
      postcode: values.postcode,
      concessionNumber: String(values.concessionNumber ?? ''),
      entitlementNumber: String(values.entitlementNumber ?? ''),
      isAbOrTorIslander: Boolean(values.isAbOrTorIslander),
    } as PatientProfileForm
    await updateProfileMutation.mutateAsync({
      profile,
      mobileVerificationToken,
    })
  }

  const isReview = page === questionnaire.pages.length
  const currentPage = isReview ? null : questionnaire.pages[page]
  const currentFields = currentPage
    ? currentPage === 'personal-information'
      ? [...PROFILE_FIELDS]
      : extractFieldIds(currentPage)
    : []
  const requiresMobileVerification =
    currentPage === 'personal-information' &&
    Boolean(values.mobile) &&
    (values.mobile !== mobileVerificationStatus.address ||
      !mobileVerificationStatus.verified)
  const totalSteps = questionnaire.pages.length + 1
  const isDetails = currentPage === 'personal-information'
  const personalDetailsPageIndex = questionnaire.pages.indexOf(
    'personal-information',
  )
  const isNavigationLoading =
    isAdvancing ||
    isSubmitting ||
    startTfaChallengeMutation.isPending ||
    verifyTfaChallengeMutation.isPending

  useEffect(() => {
    if (page > questionnaire.pages.length) {
      setPage([questionnaire.pages.length, -1])
    }
  }, [page, questionnaire.pages.length])

  function changePage(nextPage: number) {
    const boundedPage = Math.max(
      0,
      Math.min(nextPage, questionnaire.pages.length),
    )
    setPage([boundedPage, boundedPage > page ? 1 : -1])
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function goForward() {
    if (isNavigationLoading || advancingRef.current) return

    advancingRef.current = true
    setIsAdvancing(true)

    try {
      const validatedErrors = await validateForm()
      const invalidFields = currentFields.filter((id) =>
        getIn(validatedErrors, id),
      )
      if (invalidFields.length > 0) {
        await Promise.all(
          invalidFields.map((id) => setFieldTouched(id, true, false)),
        )
        return
      }

      if (requiresMobileVerification) {
        await startTfaVerification([
          {
            channel: 'Sms',
            address: values.mobile,
            verify: async (token, code) => {
              const data = await verifyTfaChallengeMutation.mutateAsync({
                data: {
                  token,
                  code,
                  channel: 'Sms',
                  address: values.mobile,
                  intent: authenticated ? 'PROFILE_UPDATE' : 'ACCOUNT_CREATION',
                },
              })
              return data.token
            },
            onSuccessfulVerification: async (verificationToken) => {
              await saveAuthenticatedProfile(verificationToken)
              if (!authenticated) {
                await setFieldValue(
                  'mobileVerificationToken',
                  verificationToken,
                )
              }
              setMobileVerificationStatus({
                verified: true,
                address: values.mobile,
              })
              changePage(page + 1)
            },
            resendCode: async () => {
              const data = await startTfaChallengeMutation.mutateAsync({
                data: {
                  address: values.mobile,
                  channel: 'Sms',
                  intent: authenticated ? 'PROFILE_UPDATE' : 'ACCOUNT_CREATION',
                },
              })
              return data.token
            },
            tokenState: {},
          },
        ])
        return
      }

      if (currentPage === 'personal-information') {
        await saveAuthenticatedProfile()
      }
      changePage(page + 1)
    } finally {
      advancingRef.current = false
      setIsAdvancing(false)
    }
  }

  const header = getStepHeader({
    page,
    currentPage,
    isReview,
    certificateType,
  })

  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        prefersReducedMotion
          ? { duration: 0 }
          : {
              duration: softMotionDuration.handoff,
              ease: softEditorialEase,
            }
      }
      className="-mt-4 min-h-[calc(100dvh-68px)] w-full bg-white text-[#0B0B0F] sm:mt-4"
    >
      <Form
        noValidate
        className="mx-auto flex w-full max-w-[680px] flex-col px-5 pb-12 pt-10 sm:px-6 sm:pb-[72px] sm:pt-[52px] lg:px-0"
      >
        <AnimatePresence
          mode="wait"
          initial={!prefersReducedMotion}
          custom={direction}
        >
          <motion.section
            key={page}
            variants={stepMotion}
            animate="animate"
            custom={direction}
            initial={prefersReducedMotion ? false : 'initial'}
            exit={prefersReducedMotion ? undefined : 'exit'}
          >
            <QuizStepHeader
              {...header}
              currentStep={page + 1}
              totalSteps={totalSteps}
            />
            {isReview ? (
              <ReviewStep
                values={values}
                certificateType={certificateType}
                multipleDays={multipleDays}
                onChangeCertificate={() => changePage(0)}
                onChangeDetails={
                  personalDetailsPageIndex >= 0
                    ? () => changePage(personalDetailsPageIndex)
                    : undefined
                }
              />
            ) : currentPage === 'personal-information' ? (
              <ProfileDetailsStep
                disabled={isSubmitting}
                emailDisabled={authenticated}
              />
            ) : currentPage && typeof currentPage !== 'string' ? (
              <ClinicalStep
                page={currentPage}
                pageIndex={page}
                certificateType={certificateType}
                multipleDays={multipleDays}
                disabled={isSubmitting}
              />
            ) : null}
          </motion.section>
        </AnimatePresence>

        <motion.div
          layout="position"
          transition={{
            layout: {
              duration: softMotionDuration.panel,
              ease: softEditorialEase,
            },
          }}
        >
          <QuizFooter
            page={page}
            isDetails={isDetails}
            isReview={isReview}
            backDisabled={page === 0 || isNavigationLoading}
            nextLoading={isNavigationLoading}
            nextDisabled={isNavigationLoading}
            onBack={() => changePage(page - 1)}
            onNext={goForward}
          />
        </motion.div>
      </Form>
    </motion.div>
  )
}

function getStepHeader({
  page,
  currentPage,
  isReview,
  certificateType,
}: {
  page: number
  currentPage: QuestionnairePage | null
  isReview: boolean
  certificateType: 'SICK' | 'CARER'
}) {
  if (isReview) {
    return {
      eyebrow: 'Certificate',
      title: 'Review your request.',
      accent: 'orange' as const,
    }
  }

  if (currentPage === 'personal-information') {
    return {
      eyebrow: 'Your details',
      title: 'Add your details.',
      accent: 'green' as const,
    }
  }

  const copy = STEP_COPY[page] ?? STEP_COPY[0]
  if (page === 0 && certificateType === 'CARER') {
    return { ...copy, title: "Let's set up your carer certificate." }
  }
  return copy
}

function QuizStepHeader({
  eyebrow,
  title,
  accent,
  currentStep,
  totalSteps,
}: {
  eyebrow: string
  title: string
  accent: 'orange' | 'green'
  currentStep: number
  totalSteps: number
}) {
  const accentClasses =
    accent === 'green'
      ? 'text-[#1F8D77] before:bg-[#38B9A4]'
      : 'text-[#B15C10] before:bg-[#F28A2B]'

  return (
    <header className="mb-5 flex flex-col gap-3 sm:mb-7 sm:gap-4">
      <div className="flex items-center justify-between gap-4">
        <p
          className={clsx(
            "flex items-center gap-2 text-[11px] font-semibold uppercase leading-4 tracking-[0.025em] before:h-[7px] before:w-[7px] before:shrink-0 before:rounded-full before:content-[''] sm:text-xs",
            accentClasses,
          )}
        >
          Request care · {eyebrow}
        </p>
        <p className="shrink-0 text-[10.5px] font-semibold uppercase leading-4 tracking-[0.06em] text-[#A0A0A8] sm:text-[11.5px]">
          Step {currentStep} of {totalSteps}
        </p>
      </div>
      <h1 className="font-display text-[26px] font-bold leading-[1.27] tracking-tight sm:text-[34px] sm:leading-[42px]">
        {title}
      </h1>
    </header>
  )
}

function ClinicalStep({
  page,
  pageIndex,
  certificateType,
  multipleDays,
  disabled,
}: {
  page: Exclude<
    QuestionnairePage,
    'personal-information' | 'upload-supporting-document' | 'call-arrangement'
  >
  pageIndex: number
  certificateType: 'SICK' | 'CARER'
  multipleDays: boolean
  disabled: boolean
}) {
  if (pageIndex === 0) {
    return (
      <motion.div variants={questionListMotion} className="flex flex-col">
        <motion.div variants={questionItemMotion} className="mb-5 sm:mb-7">
          <InfoBanner>
            This pathway is for simple certificates only — it doesn&apos;t
            replace seeing a doctor.
          </InfoBanner>
        </motion.div>
        {page.questions.map((question) => (
          <motion.div key={questionKey(question)} variants={questionItemMotion}>
            <CertificateQuestion
              question={question}
              certificateType={certificateType}
              multipleDays={multipleDays}
              disabled={disabled}
            />
          </motion.div>
        ))}
      </motion.div>
    )
  }

  if (pageIndex === 1) {
    return (
      <motion.div
        variants={questionListMotion}
        className="flex flex-col gap-8 sm:gap-9"
      >
        {page.questions.map((question) => {
          if (question.type === 'free-text') {
            return (
              <motion.div key={question.key} variants={questionItemMotion}>
                <QuizTextArea
                  name={question.key}
                  label={question.title}
                  disabled={disabled}
                />
              </motion.div>
            )
          }

          if (question.type !== 'yes-no' && question.type !== 'single-choice') {
            return null
          }

          const isChildQuestion = question.key === 'childHasSeriousSymptoms'
          const isRedFlagQuestion = question.key === 'hasSeriousSymptoms'
          const title = isRedFlagQuestion
            ? certificateType === 'CARER'
              ? 'Do they have any of these symptoms right now?'
              : 'Do you have any of these symptoms right now?'
            : isChildQuestion
              ? 'If you are caring for a child, do they have any of these symptoms?'
              : 'Do you have any significant current or past medical conditions?'

          return (
            <motion.div
              key={question.key}
              variants={questionItemMotion}
              className="flex flex-col gap-3"
            >
              <h2 className="text-[15.5px] font-semibold leading-[22px] sm:text-[16.5px] sm:leading-[23px]">
                {title}
              </h2>
              {(isRedFlagQuestion || isChildQuestion) && (
                <ul className="flex flex-col gap-2 pl-0 text-[14px] leading-5 text-[#5C5C64] sm:text-[14.5px] sm:leading-[21px]">
                  {(isChildQuestion
                    ? CHILD_SERIOUS_SYMPTOMS
                    : SERIOUS_SYMPTOMS
                  ).map((symptom) => (
                    <li key={symptom} className="flex gap-3">
                      <span
                        aria-hidden="true"
                        className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-[#A0A0A8]"
                      />
                      <span>{symptom}</span>
                    </li>
                  ))}
                </ul>
              )}
              <ChoiceField
                name={question.key}
                options={
                  question.type === 'single-choice'
                    ? question.options
                    : [
                        { id: 'Yes', label: 'Yes' },
                        { id: 'No', label: 'No' },
                      ]
                }
                columns={question.key === 'childHasSeriousSymptoms' ? 3 : 2}
                disabled={disabled}
              />
            </motion.div>
          )
        })}
      </motion.div>
    )
  }

  return (
    <motion.div
      variants={questionListMotion}
      className="flex flex-col gap-9 sm:gap-10"
    >
      {page.questions.map((question) => {
        if (question.type !== 'yes-no') return null

        const label =
          question.key === 'mcSeeGpAgreement'
            ? certificateType === 'CARER'
              ? "If they don't get better, will you take them to a GP in person within the next 24–48 hours?"
              : "If you don't get better, will you see a GP in person within the next 24–48 hours?"
            : question.key === 'mcInlandAgreement'
              ? 'Are you in Australia right now?'
              : 'Do you accept the terms and conditions?'

        return (
          <motion.div
            key={question.key}
            variants={questionItemMotion}
            className="flex flex-col gap-3"
          >
            <div>
              <h2 className="text-[15.5px] font-semibold leading-[22px] sm:text-[16.5px] sm:leading-[23px]">
                {label}
              </h2>
              {question.key === 'mcTnC' && (
                <Link
                  href="/page/terms-of-service"
                  className="mt-1 inline-flex text-[13px] font-semibold leading-4 text-[#2563EB] hover:underline"
                  target="_blank"
                >
                  Read the terms →
                </Link>
              )}
            </div>
            <ChoiceField
              name={question.key}
              options={[
                { id: 'Yes', label: 'Yes' },
                { id: 'No', label: 'No' },
              ]}
              columns={2}
              disabled={disabled}
            />
          </motion.div>
        )
      })}
    </motion.div>
  )
}

function CertificateQuestion({
  question,
  certificateType,
  multipleDays,
  disabled,
}: {
  question: Question
  certificateType: 'SICK' | 'CARER'
  multipleDays: boolean
  disabled: boolean
}) {
  if (question.type === 'date') {
    return <CertificateDateField name={question.key} disabled={disabled} />
  }

  if (question.type === 'date-range') {
    return (
      <CertificateDateRangeField
        startName={question.startKey}
        endName={question.endKey}
        disabled={disabled}
      />
    )
  }

  if (question.type === 'free-text') {
    return (
      <QuizTextArea
        name={question.key}
        label={
          certificateType === 'CARER'
            ? 'Tell us a little more about their symptoms.'
            : 'Tell us a little more about your symptoms.'
        }
        disabled={disabled}
      />
    )
  }

  if (question.type !== 'single-choice' && question.type !== 'yes-no') {
    return null
  }

  const title = getCertificateQuestionTitle(question.key, certificateType)
  const options =
    question.type === 'single-choice'
      ? question.options.map((option) => ({
          ...option,
          label: RADIO_LABELS[option.id] ?? option.label,
        }))
      : [
          { id: 'Yes', label: 'Yes' },
          { id: 'No', label: 'No' },
        ]
  const columns =
    question.key === 'symptoms' ? 2 : question.key === 'mcCarerFor' ? 3 : 2

  return (
    <div className="flex flex-col gap-3 pb-7 sm:gap-3.5 sm:pb-[26px]">
      <h2 className="text-[15.5px] font-semibold leading-[22px] sm:text-[16.5px] sm:leading-[23px]">
        {title}
      </h2>
      <ChoiceField
        name={question.key}
        options={options}
        columns={columns}
        disabled={disabled}
        stackOnMobile={question.key !== 'requiresDoctorVisit'}
      />
      {question.type === 'single-choice' && (
        <SelectedOptionDisclaimer name={question.key} options={options} />
      )}
    </div>
  )
}

function getCertificateQuestionTitle(
  key: string,
  certificateType: 'SICK' | 'CARER',
) {
  switch (key) {
    case 'mcFor':
      return "What's it for?"
    case 'mcCarerFor':
      return 'Who are you caring for?'
    case 'requiresDoctorVisit':
      return certificateType === 'CARER'
        ? 'Do they need a doctor visit or a longer telehealth consultation?'
        : "Do you feel unfit for work or study, but don't think you need a full consultation?"
    case 'symptoms':
      return certificateType === 'CARER'
        ? 'What are they suffering from?'
        : 'What are you suffering from?'
    default:
      return ''
  }
}

function InfoBanner({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 rounded-[14px] bg-[#FBF3E3] px-4 py-3.5 text-[13px] leading-[19px] text-[#8A5A1F] sm:rounded-2xl sm:px-[18px]">
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full border border-current text-[10px] font-semibold"
      >
        i
      </span>
      <p>{children}</p>
    </div>
  )
}

type ChoiceOption = {
  id: string
  label: string
  disclaimer?: string
}

function ChoiceField({
  name,
  options,
  columns,
  disabled,
  stackOnMobile = false,
}: {
  name: string
  options: ChoiceOption[]
  columns: 2 | 3
  disabled: boolean
  stackOnMobile?: boolean
}) {
  const [field, meta, helpers] = useField<string>(name)
  const gridClass =
    columns === 3
      ? 'grid-cols-1 sm:grid-cols-3'
      : stackOnMobile
        ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-2'

  return (
    <div>
      <div className={clsx('grid gap-2.5 sm:gap-3', gridClass)}>
        {options.map((option) => {
          const selected = field.value === option.id
          const invalid = selected && meta.touched && Boolean(meta.error)
          const id = `${name}-${option.id}`

          return (
            <label
              key={option.id}
              htmlFor={id}
              className={clsx(
                'relative flex min-h-[50px] cursor-pointer items-center rounded-[14px] bg-[#F4F4F6] px-4 py-3 text-[14.5px] font-normal leading-[19px] transition-[border-color,background-color,color,box-shadow] duration-300 ease-[cubic-bezier(0.22,0.55,0.28,1)] sm:min-h-[54px] sm:rounded-2xl sm:px-5',
                invalid
                  ? 'border-[1.5px] border-[#C6453C] text-[#0B0B0F]'
                  : selected
                    ? 'border-[1.5px] border-[#0B0B0F] text-[#0B0B0F]'
                    : 'border-[1.5px] border-transparent text-[#5C5C64] hover:bg-[#ECECEF]',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <input
                id={id}
                name={name}
                type="radio"
                value={option.id}
                checked={selected}
                disabled={disabled}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                onBlur={() => helpers.setTouched(true, true)}
                onChange={() => {
                  helpers.setTouched(true, false)
                  helpers.setValue(option.id, true)
                }}
              />
              <span className="min-w-0 pr-3">{option.label}</span>
              <span
                aria-hidden="true"
                className={clsx(
                  'ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors duration-300',
                  invalid
                    ? 'bg-[#C6453C]'
                    : selected
                      ? 'bg-[#0B0B0F]'
                      : 'border-[1.5px] border-[#C4C4CA] bg-white',
                )}
              >
                {selected && <span className="h-2 w-2 rounded-full bg-white" />}
              </span>
            </label>
          )
        })}
      </div>
      {meta.touched && meta.error && (
        <div className="mt-3.5 flex items-start gap-[7px] text-[#C6453C]">
          <svg
            aria-hidden="true"
            className="mt-0.5 h-3.5 w-3.5 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 8v5M12 16h.01"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="text-[13.5px] font-normal leading-[19px]">
            {meta.error}
          </p>
        </div>
      )}
    </div>
  )
}

function SelectedOptionDisclaimer({
  name,
  options,
}: {
  name: string
  options: ChoiceOption[]
}) {
  const [field] = useField<string>(name)
  const prefersReducedMotion = useReducedMotion()
  const selectedOption = options.find((option) => option.id === field.value)
  const disclaimer = selectedOption?.disclaimer

  return (
    <AnimatePresence initial={false}>
      {disclaimer && (
        <motion.div
          key={`${name}-${selectedOption.id}`}
          initial={
            prefersReducedMotion ? false : { opacity: 0, height: 0, y: -6 }
          }
          animate={{ opacity: 1, height: 'auto', y: 0 }}
          exit={
            prefersReducedMotion ? undefined : { opacity: 0, height: 0, y: -4 }
          }
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : {
                  duration: softMotionDuration.panel,
                  ease: softEditorialEase,
                }
          }
          className="overflow-hidden"
        >
          {name === 'mcFor' && selectedOption.id === 'Study' ? (
            <aside className="flex flex-col gap-1.5 rounded-2xl bg-[#E7EFFC] px-5 py-[18px]">
              <h3 className="text-[14.5px] font-semibold leading-5 text-[#1E3A6E]">
                Need it for an exam deferral?
              </h3>
              <p className="text-[13.5px] font-normal leading-5 text-[#3E5578]">
                {disclaimer}
              </p>
              <Link
                href="/telehealth-consultation/start"
                className="mt-1 text-[13.5px] font-semibold leading-5 text-[#2563EB] transition-colors hover:text-[#1D4ED8]"
              >
                Book a general consultation —{' '}
                <PricingValue name="generalConsultation" /> →
              </Link>
            </aside>
          ) : (
            <p className="rounded-[14px] bg-[#F5F7FC] px-4 py-3 text-[13px] leading-[19px] text-[#5C5C64]">
              {disclaimer}
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function CertificateDateField({
  name,
  disabled,
}: {
  name: string
  disabled: boolean
}) {
  const [field, meta, helpers] = useField<string>(name)
  const today = useMemo(() => new Date(), [])
  const options = [
    { id: formatDate(today), label: 'Today', date: format(today, 'd MMM') },
    {
      id: formatDate(addDays(today, 1)),
      label: 'Tomorrow',
      date: format(addDays(today, 1), 'd MMM'),
    },
  ]

  return (
    <div className="flex flex-col gap-3 pb-7 sm:gap-3.5 sm:pb-[26px]">
      <h2 className="text-[15.5px] font-semibold leading-[22px] sm:text-[16.5px] sm:leading-[23px]">
        Which date do you need the certificate for?
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {options.map((option) => {
          const selected = field.value === option.id
          const id = `${name}-${option.id.replaceAll('/', '-')}`
          return (
            <label
              key={option.id}
              htmlFor={id}
              className={clsx(
                'relative flex min-h-[62px] cursor-pointer items-center rounded-[14px] bg-[#F4F4F6] px-4 py-3 sm:min-h-[58px] sm:rounded-2xl sm:px-5',
                selected
                  ? 'border-[1.5px] border-[#0B0B0F]'
                  : 'border-[1.5px] border-transparent',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <input
                id={id}
                name={name}
                type="radio"
                value={option.id}
                checked={selected}
                disabled={disabled}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                onChange={() => {
                  helpers.setTouched(true, false)
                  helpers.setValue(option.id, true)
                }}
              />
              <span className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                <span
                  className={clsx(
                    'text-[14.5px] font-normal leading-[18px]',
                    selected ? 'text-[#0B0B0F]' : 'text-[#5C5C64]',
                  )}
                >
                  {option.label}
                </span>
                <span className="text-xs leading-[15px] text-[#A0A0A8] sm:text-[13.5px]">
                  {option.date}
                </span>
              </span>
              <RadioMark selected={selected} />
            </label>
          )
        })}
      </div>
      {meta.touched && meta.error && <FieldError>{meta.error}</FieldError>}
    </div>
  )
}

function CertificateDateRangeField({
  startName,
  endName,
  disabled,
}: {
  startName: string
  endName: string
  disabled: boolean
}) {
  const { setFieldValue, setValues } = useFormikContext<ProfileLikeForm>()
  const [startField, startMeta, startHelpers] = useField<string>(startName)
  const [endField, endMeta, endHelpers] = useField<string>(endName)
  const today = useMemo(() => new Date(), [])
  const options = [
    {
      id: formatDate(addDays(today, -1)),
      end: formatDate(today),
      label: 'Yesterday — today',
      date: `${format(addDays(today, -1), 'd MMM')} – ${format(today, 'd MMM')}`,
    },
    {
      id: formatDate(today),
      end: formatDate(addDays(today, 1)),
      label: 'Today — tomorrow',
      date: `${format(today, 'd MMM')} – ${format(addDays(today, 1), 'd MMM')}`,
    },
  ]

  useEffect(() => {
    if (!startField.value) return

    const derivedEndDate = formatDate(addDays(parseDate(startField.value), 1))
    if (endField.value !== derivedEndDate) {
      void setFieldValue(endName, derivedEndDate, true)
    }
  }, [endField.value, endName, setFieldValue, startField.value])

  return (
    <div className="flex flex-col gap-3 pb-7 sm:gap-3.5 sm:pb-[26px]">
      <input {...endField} type="hidden" />
      <h2 className="text-[15.5px] font-semibold leading-[22px] sm:text-[16.5px] sm:leading-[23px]">
        Which two days do you need the certificate for?
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        {options.map((option) => {
          const selected = startField.value === option.id
          const id = `${startName}-${option.id.replaceAll('/', '-')}`
          return (
            <label
              key={option.id}
              htmlFor={id}
              className={clsx(
                'relative flex min-h-[62px] cursor-pointer items-center rounded-[14px] bg-[#F4F4F6] px-4 py-3 sm:rounded-2xl sm:px-5',
                selected
                  ? 'border-[1.5px] border-[#0B0B0F]'
                  : 'border-[1.5px] border-transparent',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <input
                id={id}
                name={startName}
                type="radio"
                value={option.id}
                checked={selected}
                disabled={disabled}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                onChange={async () => {
                  await setValues(
                    (currentValues) => ({
                      ...currentValues,
                      [startName]: option.id,
                      [endName]: option.end,
                    }),
                    true,
                  )
                  await Promise.all([
                    startHelpers.setTouched(true, false),
                    endHelpers.setTouched(true, false),
                  ])
                }}
              />
              <span className="flex flex-col gap-0.5">
                <span
                  className={clsx(
                    'text-[14.5px] font-normal leading-[18px]',
                    selected ? 'text-[#0B0B0F]' : 'text-[#5C5C64]',
                  )}
                >
                  {option.label}
                </span>
                <span className="text-xs leading-[15px] text-[#A0A0A8]">
                  {option.date}
                </span>
              </span>
              <RadioMark selected={selected} />
            </label>
          )
        })}
      </div>
      {(startMeta.touched || endMeta.touched) &&
        (startMeta.error || endMeta.error) && (
          <FieldError>{startMeta.error ?? endMeta.error}</FieldError>
        )}
    </div>
  )
}

function RadioMark({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={clsx(
        'ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
        selected ? 'bg-[#0B0B0F]' : 'border-[1.5px] border-[#C4C4CA] bg-white',
      )}
    >
      {selected && <span className="h-2 w-2 rounded-full bg-white" />}
    </span>
  )
}

function QuizTextArea({
  name,
  label,
  disabled,
}: {
  name: string
  label: string
  disabled: boolean
}) {
  const [field, meta] = useField<string>(name)
  return (
    <div className="flex flex-col gap-2 pb-7">
      <label
        htmlFor={name}
        className="text-[15.5px] font-semibold leading-[22px]"
      >
        {label}
      </label>
      <textarea
        {...field}
        id={name}
        disabled={disabled}
        rows={4}
        className="min-h-28 resize-y rounded-2xl border-0 bg-[#F4F4F6] px-4 py-3.5 text-[15px] leading-5 outline-none ring-0 placeholder:text-[#A0A0A8] focus:ring-2 focus:ring-[#0B0B0F]/15 disabled:opacity-50"
      />
      {meta.touched && meta.error && <FieldError>{meta.error}</FieldError>}
    </div>
  )
}

function ProfileDetailsStep({
  disabled,
  emailDisabled,
}: {
  disabled: boolean
  emailDisabled: boolean
}) {
  const { values, setFieldValue, setFieldTouched, setValues } =
    useFormikContext<ProfileLikeForm>()

  async function handleAddressSelect(prediction: QuizAddressPrediction) {
    const parsed = parsePredictionAddress(prediction)
    await setValues(
      (currentValues) => ({
        ...currentValues,
        ...parsed,
      }),
      false,
    )

    const place = prediction.toPlace?.()
    let resolvedAddress = parsed

    if (place) {
      try {
        await place.fetchFields({
          fields: ['formattedAddress', 'addressComponents'],
        })
        resolvedAddress = parsePlaceAddress(place, prediction)
      } catch {
        // The prediction remains available for manual completion.
      }
    }

    await setValues(
      (currentValues) => ({
        ...currentValues,
        ...resolvedAddress,
      }),
      true,
    )
  }

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-[18px] sm:gap-x-4 sm:gap-y-5">
      <QuizTextField
        name="firstName"
        label="First name"
        disabled={disabled}
        className="col-span-2 sm:col-span-1"
      />
      <QuizTextField
        name="lastName"
        label="Last name"
        disabled={disabled}
        className="col-span-2 sm:col-span-1"
      />
      <QuizSelectField
        name="gender"
        label="Gender"
        placeholder="Select gender"
        options={[
          { id: 'M', label: 'Male' },
          { id: 'F', label: 'Female' },
        ]}
        disabled={disabled}
        className="col-span-2"
      />
      <QuizTextField
        name="mobile"
        label="Mobile"
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        placeholder="0400 000 000"
        disabled={disabled}
        className="col-span-2 sm:col-span-1"
      />
      <QuizTextField
        name="email"
        label="Email"
        type="email"
        autoComplete="email"
        placeholder="you@email.com"
        disabled={disabled || emailDisabled}
        className="col-span-2 sm:col-span-1"
      />
      <DobField disabled={disabled} />
      {WebConfig.prescriptionConformanceMode &&
        (['concessionNumber', 'entitlementNumber'] as const).map((name) => (
          <QuizTextField
            key={name}
            name={name}
            label={
              name === 'concessionNumber'
                ? 'Concession number'
                : 'Entitlement number'
            }
            disabled={disabled}
            className="col-span-2"
          />
        ))}
      <div className="col-span-2">
        <QuizAddressAutocomplete
          id="formattedAddress"
          name="formattedAddress"
          label="Address"
          value={values.formattedAddress}
          placeholder="Start typing your address…"
          disabled={disabled}
          className="[&_label+div>div:first-child]:h-[52px] [&_label+div>div:first-child]:rounded-[14px] [&_label+div>div:first-child]:border-0 [&_label+div>div:first-child]:bg-[#F4F4F6] [&_label+div>div:first-child]:px-4 [&_label+div>div:first-child]:shadow-none [&_label]:mb-2 [&_label]:text-[13.5px] [&_label]:leading-[17px] [&_label]:text-[#0B0B0F]"
          onValueChange={(value) => {
            setFieldValue('formattedAddress', value, true)
            setFieldTouched('formattedAddress', true, false)
          }}
          onSelect={handleAddressSelect}
          onManualEntry={(value) => {
            setFieldValue('formattedAddress', value, true)
            setFieldTouched('formattedAddress', true, true)
          }}
        />
        <FormikError name="formattedAddress" />
      </div>
      <QuizTextField
        name="suburb"
        label="Suburb"
        autoComplete="address-level2"
        disabled={disabled}
        className="col-span-2 sm:col-span-1"
      />
      <div className="col-span-2 grid grid-cols-2 gap-4 sm:col-span-1">
        <QuizSelectField
          name="state"
          label="State"
          placeholder="Pick"
          options={AU_STATES.map((state) => ({ id: state, label: state }))}
          disabled={disabled}
        />
        <QuizTextField
          name="postcode"
          label="Postcode"
          inputMode="numeric"
          autoComplete="postal-code"
          disabled={disabled}
        />
      </div>
    </div>
  )
}

function QuizTextField({
  name,
  label,
  className,
  ...props
}: {
  name: string
  label: string
  className?: string
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name'>) {
  const [field, meta] = useField<string>(name)
  return (
    <div className={clsx('col-span-1 min-w-0', className)}>
      <label
        htmlFor={name}
        className="mb-2 block text-[13.5px] font-semibold leading-[17px]"
      >
        {label}
      </label>
      <input
        {...field}
        {...props}
        id={name}
        className={clsx(
          'h-[52px] w-full rounded-[14px] border-0 bg-[#F4F4F6] px-4 text-[15px] leading-5 text-[#0B0B0F] outline-none placeholder:text-[#A0A0A8] focus:ring-2 focus:ring-[#0B0B0F]/15 disabled:cursor-not-allowed disabled:opacity-50',
          meta.touched && meta.error && 'ring-1 ring-[#C73838]',
        )}
      />
      {meta.touched && meta.error && <FieldError>{meta.error}</FieldError>}
    </div>
  )
}

function QuizSelectField({
  name,
  label,
  options,
  placeholder,
  disabled,
  className,
}: {
  name: string
  label: string
  options: Array<{ id: string; label: string }>
  placeholder: string
  disabled: boolean
  className?: string
}) {
  const [field, meta] = useField<string>(name)
  return (
    <div className={clsx('min-w-0', className)}>
      <label
        htmlFor={name}
        className="mb-2 block text-[13.5px] font-semibold leading-[17px]"
      >
        {label}
      </label>
      <select
        {...field}
        id={name}
        disabled={disabled}
        className={clsx(
          'h-[52px] w-full appearance-none rounded-[14px] border-0 bg-[#F4F4F6] bg-[linear-gradient(45deg,transparent_50%,#82828B_50%),linear-gradient(135deg,#82828B_50%,transparent_50%)] bg-[size:4px_4px,4px_4px] bg-[position:calc(100%-18px)_23px,calc(100%-14px)_23px] bg-no-repeat px-4 pr-9 text-[15px] leading-5 outline-none focus:ring-2 focus:ring-[#0B0B0F]/15 disabled:opacity-50',
          !field.value && 'text-[#A0A0A8]',
          meta.touched && meta.error && 'ring-1 ring-[#C73838]',
        )}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      {meta.touched && meta.error && <FieldError>{meta.error}</FieldError>}
    </div>
  )
}

function DobField({ disabled }: { disabled: boolean }) {
  const [field, meta, helpers] = useField<string>('dob')
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const parsedDate = field.value ? parseDate(field.value) : null
  const currentValue =
    parsedDate && isValid(parsedDate)
      ? {
          year: parsedDate.getFullYear(),
          month: parsedDate.getMonth() + 1,
          day: parsedDate.getDate(),
        }
      : undefined

  useEffect(() => {
    if (!open) return
    function closeOnOutsideClick(event: PointerEvent) {
      if (
        event.target instanceof Node &&
        !rootRef.current?.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    return () =>
      document.removeEventListener('pointerdown', closeOnOutsideClick)
  }, [open])

  function selectDate(value: DatePickerCalendarValue) {
    helpers.setTouched(true, false)
    helpers.setValue(
      formatDate(new Date(value.year, value.month - 1, value.day)),
      true,
    )
    setOpen(false)
  }

  return (
    <div ref={rootRef} className="relative col-span-2">
      <label
        htmlFor="dob"
        className="mb-2 block text-[13.5px] font-semibold leading-[17px]"
      >
        Date of birth
      </label>
      <button
        id="dob"
        type="button"
        aria-label="Date of birth"
        aria-expanded={open}
        disabled={disabled}
        className={clsx(
          'flex h-[52px] w-full items-center rounded-[14px] border-0 bg-[#F4F4F6] px-4 text-left text-[15px] leading-5 outline-none focus:ring-2 focus:ring-[#0B0B0F]/15 disabled:opacity-50',
          !currentValue && 'text-[#A0A0A8]',
          meta.touched && meta.error && 'ring-1 ring-[#C73838]',
        )}
        onClick={() => setOpen((current) => !current)}
      >
        {currentValue && parsedDate
          ? format(parsedDate, 'd MMMM yyyy')
          : 'Pick a date'}
        <CalendarDaysIcon
          aria-hidden="true"
          className="ml-auto h-4 w-4 text-[#82828B]"
        />
      </button>
      {open && (
        <div className="absolute left-0 top-[82px] z-50 sm:left-auto sm:right-0">
          <DatePickerCalendar
            value={currentValue}
            defaultValue={currentValue}
            startYear={new Date().getFullYear() - 100}
            endYear={new Date().getFullYear() - 18}
            initialView="year"
            onChange={selectDate}
          />
        </div>
      )}
      {meta.touched && meta.error && <FieldError>{meta.error}</FieldError>}
    </div>
  )
}

function FormikError({ name }: { name: string }) {
  const [, meta] = useField(name)
  if (!meta.touched || !meta.error) return null
  return <FieldError>{meta.error}</FieldError>
}

function FieldError({ children }: { children?: ReactNode }) {
  return (
    <p className="mt-2 text-[13px] font-semibold leading-[18px] text-[#C73838]">
      {children}
    </p>
  )
}

function parsePredictionAddress(prediction: QuizAddressPrediction) {
  const normalizedSecondaryText = prediction.secondaryText
    .replace(/,?\s*Australia$/i, '')
    .replace(/,\s*/g, ' ')
    .trim()
  const match = normalizedSecondaryText.match(
    /^(.*)\s+(ACT|NSW|NT|QLD|SA|TAS|VIC|WA)\s+(\d{4})$/,
  )
  return {
    formattedAddress: prediction.mainText || prediction.description,
    suburb: match?.[1] ?? '',
    state: match?.[2] ?? '',
    postcode: match?.[3] ?? '',
  }
}

function parsePlaceAddress(
  place: {
    formattedAddress?: string | null
    addressComponents?: readonly unknown[] | null
  },
  prediction: QuizAddressPrediction,
) {
  const fallback = parsePredictionAddress(prediction)
  let suburb = fallback.suburb
  let state = fallback.state
  let postcode = fallback.postcode
  let subpremise = ''
  let streetNumber = ''
  let route = ''

  for (const rawComponent of place.addressComponents ?? []) {
    const component = rawComponent as {
      types?: string[]
      shortText?: string
      longText?: string
      short_name?: string
      long_name?: string
    }
    const shortValue = component.shortText ?? component.short_name ?? ''
    const longValue = component.longText ?? component.long_name ?? shortValue
    if (
      component.types?.some((type) =>
        ['locality', 'postal_town', 'sublocality_level_1'].includes(type),
      )
    ) {
      suburb = longValue
    }
    if (component.types?.includes('administrative_area_level_1')) {
      state = shortValue
    }
    if (component.types?.includes('postal_code')) postcode = shortValue
    if (component.types?.includes('subpremise')) subpremise = longValue
    if (component.types?.includes('street_number')) streetNumber = longValue
    if (component.types?.includes('route')) route = longValue
  }

  const streetAddress = [streetNumber, route].filter(Boolean).join(' ')
  const formattedAddress =
    subpremise && streetAddress
      ? `${subpremise}/${streetAddress}`
      : streetAddress || fallback.formattedAddress

  return {
    formattedAddress,
    suburb,
    state,
    postcode,
  }
}

function ReviewStep({
  values,
  certificateType,
  multipleDays,
  onChangeCertificate,
  onChangeDetails,
}: {
  values: ProfileLikeForm
  certificateType: 'SICK' | 'CARER'
  multipleDays: boolean
  onChangeCertificate: () => void
  onChangeDetails?: () => void
}) {
  const symptom =
    values.symptoms === 'OTHERS'
      ? String(values.mcSymptom || 'Other mild symptoms')
      : (SYMPTOM_LABELS[String(values.symptoms)] ??
        String(values.symptoms || ''))
  const certificateRows = [
    {
      label: multipleDays ? 'Certificate dates' : 'Certificate date',
      value: multipleDays
        ? `${displayDate(String(values.onDate))} – ${displayDate(String(values.toDate))}`
        : displayDate(String(values.onDate)),
    },
    {
      label: "It's for",
      value: RADIO_LABELS[String(values.mcFor)] ?? String(values.mcFor),
    },
    certificateType === 'CARER'
      ? {
          label: 'Caring for',
          value:
            CarerForMap[values.mcCarerFor as keyof typeof CarerForMap] ??
            String(values.mcCarerFor || ''),
        }
      : null,
    {
      label:
        certificateType === 'CARER'
          ? 'No full consultation needed'
          : 'Feeling unfit, no full consultation needed',
      value:
        certificateType === 'CARER'
          ? values.requiresDoctorVisit === 'No'
            ? 'Yes'
            : 'No'
          : String(values.requiresDoctorVisit || ''),
    },
    { label: 'Suffering from', value: symptom },
    {
      label: 'Red-flag symptoms right now',
      value: values.hasSeriousSymptoms === 'No' ? 'None' : 'Yes',
    },
    values.hasPastMedicalConditions
      ? {
          label: 'Significant current or past conditions',
          value:
            values.hasPastMedicalConditions === 'Yes'
              ? String(values.pastMedicalConditions || 'Yes')
              : 'No',
        }
      : null,
    {
      label: 'Will see a GP in person if it doesn’t improve',
      value: String(values.mcSeeGpAgreement || ''),
    },
    {
      label: 'In Australia right now',
      value: String(values.mcInlandAgreement || ''),
    },
    {
      label: 'Terms and conditions',
      value: values.mcTnC === 'Yes' ? 'Accepted' : String(values.mcTnC || ''),
    },
  ].filter(Boolean) as Array<{ label: string; value: string }>

  const fullAddress = [
    values.formattedAddress,
    values.suburb,
    values.state,
    values.postcode,
  ]
    .filter(Boolean)
    .join(', ')
    .replace(/, ([A-Z]{2,3}), (\d{4})$/, ' $1 $2')

  const detailsRows = [
    { label: 'Name', value: `${values.firstName} ${values.lastName}` },
    {
      label: 'Gender',
      value: GENDERS[values.gender as keyof typeof GENDERS] ?? values.gender,
    },
    { label: 'Email', value: values.email },
    { label: 'Mobile', value: values.mobile },
    { label: 'Date of birth', value: displayDate(values.dob) },
    { label: 'Address', value: fullAddress },
  ]

  return (
    <QuizReviewSections>
      <QuizReviewSection
        last={!onChangeDetails}
        title="Your certificate"
        rows={certificateRows}
        onChange={onChangeCertificate}
      />
      {onChangeDetails && (
        <QuizReviewSection
          last
          title="Your details"
          rows={detailsRows}
          onChange={onChangeDetails}
        />
      )}
    </QuizReviewSections>
  )
}

function displayDate(value: string) {
  if (!value) return ''
  const parsed = parseDate(value)
  return isValid(parsed) ? format(parsed, 'EEE · d MMM yyyy') : value
}

function QuizFooter({
  page,
  isDetails,
  isReview,
  backDisabled,
  nextDisabled,
  nextLoading,
  onBack,
  onNext,
}: {
  page: number
  isDetails: boolean
  isReview: boolean
  backDisabled: boolean
  nextDisabled: boolean
  nextLoading: boolean
  onBack: () => void
  onNext: () => void
}) {
  const prefersReducedMotion = useReducedMotion()
  const showBackButton = page > 0
  const nextLabel = isDetails
    ? 'Review my request →'
    : isReview
      ? 'Continue to payment →'
      : 'Next →'

  return (
    <footer className="mt-9 flex flex-col items-center gap-2.5 sm:mt-10">
      <AnimatePresence initial={!prefersReducedMotion} mode="wait">
        <motion.div
          key={`${showBackButton ? 'with-back' : 'without-back'}-${isReview ? 'review' : 'next'}`}
          variants={footerActionsMotion}
          initial={prefersReducedMotion ? false : 'initial'}
          animate="animate"
          exit={prefersReducedMotion ? undefined : 'exit'}
          className="flex w-full flex-col-reverse gap-2.5 sm:w-auto sm:flex-row sm:gap-3"
        >
          {showBackButton && (
            <button
              type="button"
              disabled={backDisabled}
              className="flex min-h-12 w-full min-w-[144px] items-center justify-center rounded-full border-[1.5px] border-[#0B0B0F]/20 px-7 text-[14.5px] font-semibold leading-[18px] transition-colors duration-300 hover:border-[#0B0B0F]/45 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
              onClick={onBack}
            >
              ←&nbsp;&nbsp;Back
            </button>
          )}
          {isReview ? (
            <button
              id="questionnaire-submit-button"
              type="submit"
              disabled={nextDisabled}
              aria-busy={nextLoading}
              className="flex min-h-12 w-full min-w-[180px] items-center justify-center gap-2 rounded-full bg-[#0B0B0F] px-7 text-[14.5px] font-semibold leading-[18px] text-white transition-colors duration-300 hover:bg-[#24242A] disabled:cursor-not-allowed disabled:bg-[#D6D6DC] sm:w-auto"
            >
              {nextLoading && <Spinner size={4} />}
              {nextLabel}
            </button>
          ) : (
            <button
              id="questionnaire-next-button"
              type="button"
              disabled={nextDisabled}
              aria-busy={nextLoading}
              className="flex min-h-12 w-full min-w-[144px] items-center justify-center gap-2 rounded-full bg-[#0B0B0F] px-7 text-[14.5px] font-semibold leading-[18px] text-white transition-colors duration-300 hover:bg-[#24242A] disabled:cursor-not-allowed disabled:bg-[#D6D6DC] sm:w-auto"
              onClick={onNext}
            >
              {nextLoading && <Spinner size={4} />}
              {nextLabel}
            </button>
          )}
        </motion.div>
      </AnimatePresence>
      {(isDetails || isReview) && (
        <p className="mt-0.5 flex items-center gap-1.5 text-center text-xs leading-4 text-[#A0A0A8]">
          <LockClosedIcon aria-hidden="true" className="h-[11px] w-[11px]" />
          {isReview
            ? 'Your answers are only ever shared with your doctor.'
            : 'Encrypted, and only ever shared with your doctor.'}
        </p>
      )}
      <span className="sr-only">Questionnaire page {page + 1}</span>
    </footer>
  )
}

function questionKey(question: Question) {
  switch (question.type) {
    case 'date-range':
      return `${question.startKey}-${question.endKey}`
    case 'duration':
      return `${question.yearKey}-${question.monthKey}`
    default:
      return question.key
  }
}
